import { Button, DialogContent, Typography } from 'omakase';
import { RouteComponentProps } from 'types';
import { makeSigninLinkIncludingSearch } from 'components/sign-in/Signin';
import Anchor from 'components/Anchor';
import BaseComponent from '@tock/shared/components/BaseComponent';
import ButtonGroup from '@tock/shared/components/buttons/ButtonGroup';
import React from 'react';

interface Props {
  businessName?: string;
  businessEmail?: string | null;
  isOfferingEvent: boolean;
  isPrivateExperience: boolean;
  isAuthenticated: boolean;
  isWidget: boolean;
  location: RouteComponentProps<{}>['location'];
  onButtonClick(): void;
}

const AnchorWrapper: React.FunctionComponent<
  React.ComponentPropsWithoutRef<'a'> & { isWidget: boolean }
> = ({ isWidget, children, ...rest }) => {
  if (isWidget) {
    // The Anchor component can get confused about the fact that the widget is presented
    // in an iframe, but not an iframe that will be responsive to messages about sent via
    // window.postMessage() about updating its URL.
    //
    // Moreover, for a link like this, the only advantage that going through Anchor gives
    // is potentially doing analytics tracking, but we don't do that in this component as
    // we never set the trackingCategory.
    //
    // Finally, for login links we do not really want to present a login URL unless the
    // URL shown in the URL bar is clearly an exploretock.com page.
    //
    // So if we are in the widget, do not render this through the Anchor component; use a simple
    // <a target="_blank"> element instead.
    return (
      <a target="_blank" {...rest}>
        {children}
      </a>
    );
  } else {
    return <Anchor {...rest}>{children}</Anchor>;
  }
};
export default class ExperienceNotFound extends BaseComponent<Props> {
  render() {
    const {
      businessEmail,
      businessName,
      isAuthenticated,
      isOfferingEvent,
      isPrivateExperience,
      isWidget,
      location,
      onButtonClick,
    } = this.props;
    const contactLink = `mailto:${businessEmail}?subject=Access Inquiry`;
    const eventOrReservation = isOfferingEvent ? 'event' : 'reservation';
    const businessString = businessName || 'the business';
    // The trailing line break is appropriate for both places this is used:
    const availabilityText = (
      <>
        This {eventOrReservation} is available only to select users.
        <br />
      </>
    );
    return (
      <DialogContent disablePadding>
        <div className="SearchModal-body">
          <p className="Consumer-reservationParagraph">
            {!isAuthenticated && isPrivateExperience && (
              <Typography color="inherit" component="span" variant="body02">
                {availabilityText}
                {isOfferingEvent ? 'To reserve this event' : 'To make this reservation'}, try{' '}
                <AnchorWrapper
                  href={makeSigninLinkIncludingSearch('login', location)}
                  className="u-activeColor"
                  isWidget={isWidget}
                >
                  logging&nbsp;in&nbsp;to&nbsp;Tock
                </AnchorWrapper>{' '}
                or creating a new profile.
              </Typography>
            )}
            {isAuthenticated && isPrivateExperience && (
              <Typography color="inherit" component="span" variant="body02">
                {availabilityText}
                Please{' '}
                {businessEmail ? (
                  <AnchorWrapper
                    isWidget={isWidget}
                    href={contactLink}
                    target="_blank"
                    className="u-activeColor"
                  >
                    contact {businessString}
                  </AnchorWrapper>
                ) : (
                  `contact ${businessString}`
                )}{' '}
                if you believe this is an error.
              </Typography>
            )}
            {!isPrivateExperience && (
              <Typography color="inherit" component="span" variant="body02">
                We couldn't find this {eventOrReservation}! This is probably because it is no longer
                available or has not yet been made available.
              </Typography>
            )}
          </p>
          <ButtonGroup className="SearchModal-buttonGroup">
            <Button type="submit" size="medium" variant="primary" fullWidth onClick={onButtonClick}>
              Close
            </Button>
          </ButtonGroup>
        </div>
      </DialogContent>
    );
  }
}
